import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  type ReactNode,
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  getSessionState,
  transitionSession,
  canAuthenticate,
  startAuthentication,
  useAuthSession,
} from '@/entities/user/session';
import {
  type AuthRequest,
  LDAPApi,
  LDAPServerDTO,
  LoginStatus,
  OIDCApi,
} from '@/entities/user/user';
import { getAuthRedirectUrls } from '@/shared/lib/utils/auth-redirect';
import type { InterceptedError } from '@/shared/api/intercepted-error';
import { messageError, requestError } from '@/shared/ui/notifications';
import { useLogin, useLogout } from '../model/use-authentication';
import {
  Affix,
  Button,
  Divider,
  Group,
  Loader,
  PasswordInput,
  ScrollArea,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useLocalStorage } from '@mantine/hooks';
import IconBrandOauth from '@tabler/icons-react/dist/esm/icons/IconBrandOauth.mjs';
import IconLock from '@tabler/icons-react/dist/esm/icons/IconLock.mjs';
import IconServer from '@tabler/icons-react/dist/esm/icons/IconServer.mjs';
import IconServerCog from '@tabler/icons-react/dist/esm/icons/IconServerCog.mjs';
import IconUser from '@tabler/icons-react/dist/esm/icons/IconUser.mjs';
import { z } from 'zod/v4';

interface LoginFormProps {
  languageSelector?: ReactNode;
  loginPage?: boolean;
  prepareApplication: (queryClient: QueryClient) => Promise<boolean>;
  Preparation: ComponentType<{ onRetry: () => void; onLogout: () => void }>;
}

function LoginForm({
  languageSelector,
  loginPage,
  prepareApplication,
  Preparation,
}: LoginFormProps) {
  const { t } = useTranslation();
  const { loginStatus } = useAuthSession();
  const queryClient = useQueryClient();
  const [defaultLDAP, setDefaultLDAP] = useLocalStorage<string | null>({
    key: 'default-ldap',
    defaultValue: null,
  });
  const [domainChanged, setDomainChanged] = useState(false);
  const form = useForm<AuthRequest>({
    initialValues: { ldap_id: undefined, login: '', password: '' },
    validate: {
      login: (value) =>
        value.length === 0 ? t('form.required', { ns: 'validation' }) : null,
      password: (value) =>
        value.length === 0 ? t('form.required', { ns: 'validation' }) : null,
    },
  });
  const [searchParams] = useSearchParams();
  const getRedirectUrl = useCallback(
    () => getAuthRedirectUrls(searchParams),
    [searchParams],
  );
  const {
    login,
    isPending: isLoggingIn,
    retryPreparation,
  } = useLogin(form, prepareApplication, loginPage, getRedirectUrl);
  const logout = useLogout(false);

  const providers = useQuery({
    queryKey: ['oidc-active-providers'],
    queryFn: ({ signal }) =>
      OIDCApi.getActiveProviderNames({ signal, timeout: 15000 }).then(
        (res) => res.data,
      ),
    retry: false,
    useErrorBoundary: false,
    onError: requestError,
  });
  const ldap = useQuery({
    queryKey: ['ldap-list'],
    queryFn: ({ signal }) =>
      LDAPApi.getActiveServers({ signal, timeout: 15000 }).then(
        (res) => res.data,
      ),
    retry: false,
    useErrorBoundary: false,
    onError: requestError,
  });
  const ldapServers = useMemo(() => {
    const parsed = z.array(LDAPServerDTO).safeParse(ldap.data);
    return parsed.success ? parsed.data : [];
  }, [ldap.data]);
  const ldapOptions = ldapServers.map((server) => ({
    label: server.display_name ?? '',
    value: server.id ?? '',
  }));

  useEffect(() => {
    // Restore a remembered domain only while the user has not started entering credentials.
    if (
      !domainChanged &&
      !form.isTouched('login') &&
      !form.isTouched('password') &&
      defaultLDAP &&
      ldapServers.some((server) => server.id === defaultLDAP)
    ) {
      form.setFieldValue('ldap_id', defaultLDAP);
    }
  }, [defaultLDAP, ldapServers, domainChanged]);

  const {
    mutate: checkAndRedirectOidc,
    isPending: isOidcChecking,
    isSuccess: isOidcRedirecting,
    variables: oidcCheckingProvider,
  } = useMutation({
    retry: false,
    mutationFn: async ({
      idpName,
    }: { idpName: string } & NonNullable<
      ReturnType<typeof startAuthentication>
    >) => {
      const { redirect_url, merge_redirect_url } = getRedirectUrl();
      await OIDCApi.checkLoginEndpoint(idpName, redirect_url, false);
      return { idpName, redirect_url, merge_redirect_url };
    },
    onError: (error: InterceptedError, { session, previous }) => {
      if (getSessionState(queryClient) !== session) return;
      transitionSession(queryClient, previous.loginStatus);
      requestError(error);
    },
    onSuccess: ({ idpName, redirect_url, merge_redirect_url }, { session }) => {
      if (getSessionState(queryClient) !== session) return;
      window.location.href = OIDCApi.getLoginUrl(
        idpName,
        redirect_url,
        merge_redirect_url,
        false,
      );
    },
  });
  const isPreparing = [
    LoginStatus.preparing,
    LoginStatus.preparationError,
  ].includes(loginStatus);
  const isBusy = !canAuthenticate(loginStatus);
  useEffect(() => {
    const error = searchParams.get('error');
    if (error)
      messageError(error, {
        id: `oidc-callback-${error}`,
        title: t('oidc.connectionError', { ns: 'login' }),
        message: error,
      });
  }, [searchParams, t]);

  if (isPreparing) {
    return (
      <Stack w='100%' mih={400} justify='center'>
        <Preparation
          onRetry={() => void retryPreparation()}
          onLogout={logout}
        />
      </Stack>
    );
  }

  const providerNames = providers.data ?? [];
  return (
    <Stack w='100%'>
      <Title order={5} className='uppercase text-center' c='blue.8'>
        {t('app.name')}
      </Title>
      {languageSelector ? (
        <Affix position={{ bottom: 25, right: 25 }} zIndex={100}>
          {languageSelector}
        </Affix>
      ) : null}
      <form
        data-testid='login-form'
        onSubmit={form.onSubmit((values) => {
          if (isBusy) return;
          login(values);
        })}
      >
        <Stack>
          <Select
            {...form.getInputProps('ldap_id')}
            value={form.values.ldap_id ?? ''}
            onChange={(value) => {
              setDomainChanged(true);
              form.setFieldValue('ldap_id', value || undefined);
              setDefaultLDAP(value || null);
            }}
            disabled={isBusy}
            leftSection={<IconServerCog size={16} />}
            rightSection={
              ldap.isFetching ? (
                <Loader size='xs' data-testid='ldap-loader' />
              ) : undefined
            }
            placeholder={t('domain', { ns: 'login' })}
            data-testid='login-form-ldap-select'
            data={[
              { value: '', label: t('without-ldap', { ns: 'login' }) },
              ...ldapOptions,
            ]}
            attributes={{
              dropdown: { 'data-testid': 'login-form-ldap-select-dropdown' },
            }}
            renderOption={({ option }) => (
              <Group
                data-testid={`login-domain-${option.value || 'local'}`}
                justify='space-between'
                align='center'
                className='w-full'
              >
                <Group align='center' gap='xs'>
                  <IconServer size={16} />
                  <Text size='sm'>{option.label}</Text>
                </Group>
                <Text size='xs' c='dimmed'>
                  {ldapServers.find((server) => server.id === option.value)
                    ?.domain || ''}
                </Text>
              </Group>
            )}
          />
          {ldap.isError && (
            <Button
              data-testid='ldap-retry'
              disabled={isBusy}
              loading={ldap.isFetching}
              onClick={() => void ldap.refetch()}
            >
              {t('retryLdap', { ns: 'login' })}
            </Button>
          )}
          <TextInput
            autoFocus
            data-testid='login-form-name'
            {...form.getInputProps('login')}
            required
            disabled={isBusy}
            leftSection={<IconUser size={16} />}
            placeholder={t('login', { ns: 'login' })}
            autoComplete='username'
            name='username'
          />
          <PasswordInput
            data-testid='login-form-password'
            {...form.getInputProps('password')}
            required
            disabled={isBusy}
            leftSection={<IconLock size={16} />}
            placeholder={t('password', { ns: 'login' })}
            autoComplete='current-password'
            name='password'
          />
          <Button
            data-testid='login-submit'
            variant='filled'
            type='submit'
            loading={isLoggingIn}
            disabled={isBusy}
          >
            {t('loginBtn', { ns: 'login' })}
          </Button>
          {(providers.isLoading ||
            providers.isError ||
            providerNames.length > 0) && (
            <Stack gap='xs' data-testid='oidc-methods'>
              <Divider
                label={t('or-sign-in-with', { ns: 'login' })}
                labelPosition='center'
              />
              {providers.isLoading ? (
                <Stack
                  gap='xs'
                  data-testid='oidc-loader'
                  aria-label={t('loadingMethods', { ns: 'login' })}
                >
                  <Skeleton height={36} />
                  <Skeleton height={36} />
                </Stack>
              ) : providers.isError ? (
                <Button
                  data-testid='oidc-retry'
                  disabled={isBusy}
                  loading={providers.isFetching}
                  onClick={() => void providers.refetch()}
                >
                  {t('retryOidc', { ns: 'login' })}
                </Button>
              ) : (
                <ScrollArea.Autosize mah={100}>
                  <SimpleGrid
                    cols={providerNames.length > 2 ? 2 : 1}
                    spacing='xs'
                  >
                    {providerNames.map((provider) => (
                      <Button
                        key={provider.name}
                        data-testid={`oidc-provider-${provider.name}`}
                        variant='default'
                        leftSection={<IconBrandOauth size={16} />}
                        onClick={() => {
                          if (isBusy) return;
                          const attempt = startAuthentication(queryClient);
                          if (attempt)
                            checkAndRedirectOidc({
                              idpName: provider.name,
                              ...attempt,
                            });
                        }}
                        loading={
                          (isOidcChecking ||
                            (isOidcRedirecting &&
                              loginStatus === LoginStatus.authenticating)) &&
                          oidcCheckingProvider?.idpName === provider.name
                        }
                        disabled={isBusy}
                      >
                        {provider.display_name}
                      </Button>
                    ))}
                  </SimpleGrid>
                </ScrollArea.Autosize>
              )}
            </Stack>
          )}
        </Stack>
      </form>
    </Stack>
  );
}

export default LoginForm;
